import {CATEGORIAS_FOLHA} from "../../shared/categoriasFolha";
import {useState} from "react";
import {Link as RouterLink} from "react-router-dom";
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    Link,
    MenuItem,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {useFolhasPreparadas} from "../../api/DpgeIntegracaoEsfingeApi";
import {consolidarFolhaCompetencia} from "../../shared/resumoFolhaCompetencia";
import {formatarCompetencia} from "../../shared/ocorrenciaTemporalUtil";

const moeda = (centavos) =>
    new Intl.NumberFormat("pt-BR", {style: "currency", currency: "BRL"}).format(
        centavos / 100
    );
const ESTADOS = {
    AGUARDANDO_APROVACAO: "Aguardando aprovação",
    EM_FILA: "Em fila",
    PROCESSAMENTO: "Em processamento",
    PROCESSADO_COM_ERRO: "Erro no envio",
    RESULTADO_INDETERMINADO: "Resultado indeterminado"
};

export default function ResumoFolhaCompetencia({
    competencia,
    ocorrenciaAtualId
}) {
    const {data: folhas = [], isLoading, error} = useFolhasPreparadas();
    const [selecionada, setSelecionada] = useState("");
    const [categoria, setCategoria] = useState("TODAS");
    const competencias = [...new Set(folhas.map((item) => item.competencia))]
        .sort()
        .reverse();
    const efetiva =
        competencia ||
        (competencias.includes(selecionada)
            ? selecionada
            : [...folhas].sort((a, b) => Number(b.id) - Number(a.id))[0]
                  ?.competencia);
    const grupos = consolidarFolhaCompetencia(folhas, efetiva, categoria);
    const conflito = grupos.some((grupo) => grupo.conflito);
    const totalP = conflito
        ? null
        : grupos.reduce((total, item) => total + item.proventosCentavos, 0);
    const totalD = conflito
        ? null
        : grupos.reduce((total, item) => total + item.descontosCentavos, 0);
    return (
        <Card
            variant="outlined"
            sx={{borderRadius: 2}}
            aria-label="Resumo das folhas por competência"
        >
            <CardContent>
                <Stack
                    direction={{xs: "column", sm: "row"}}
                    justifyContent="space-between"
                    spacing={2}
                    sx={{mb: 2}}
                >
                    <Box>
                        <Typography variant="h6">
                            Folhas da competência por grupo de beneficiários
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Valores de referência da competência, com a situação
                            de envio de cada grupo.
                        </Typography>
                    </Box>
                    {competencia ? (
                        <Chip
                            label={`Competência: ${formatarCompetencia(
                                competencia
                            )}`}
                        />
                    ) : (
                        competencias.length > 0 && (
                            <TextField
                                select
                                size="small"
                                label="Competência da folha"
                                value={efetiva || ""}
                                onChange={(e) => setSelecionada(e.target.value)}
                                sx={{minWidth: 180}}
                            >
                                {competencias.map((item) => (
                                    <MenuItem key={item} value={item}>
                                        {formatarCompetencia(item)}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )
                    )}
                </Stack>
                <TextField
                    select
                    size="small"
                    label="Categoria da folha"
                    value={categoria}
                    onChange={(e) => setCategoria(e.target.value)}
                    sx={{mb: 2, minWidth: 240}}
                >
                    <MenuItem value="TODAS">
                        Todas as folhas da competência
                    </MenuItem>
                    {[
                        ...new Set([
                            ...Object.keys(CATEGORIAS_FOLHA),
                            ...folhas
                                .map((f) => f.categoriaOrigem)
                                .filter(Boolean)
                        ])
                    ].map((item) => (
                        <MenuItem key={item} value={item}>
                            {CATEGORIAS_FOLHA[item] || item}
                        </MenuItem>
                    ))}
                </TextField>
                {isLoading ? (
                    <CircularProgress
                        size={24}
                        aria-label="Carregando valores dos grupos"
                    />
                ) : error ? (
                    <Alert severity="error">
                        {error.message ||
                            "Não foi possível conferir os valores dos grupos da folha."}
                    </Alert>
                ) : !efetiva ? (
                    <Alert severity="info">
                        Nenhuma folha preparada para conferência.
                    </Alert>
                ) : (
                    <>
                        {conflito && (
                            <Alert severity="warning" sx={{mb: 2}}>
                                Há ocorrências com a mesma competência,
                                sequência e tipo, ou sem sequência válida.
                                Confira as ocorrências indicadas; os totais
                                afetados ficam indisponíveis até resolver o
                                conflito.
                            </Alert>
                        )}
                        <Grid container spacing={2}>
                            {grupos.map((grupo) => (
                                <Grid item xs={12} md={4} key={grupo.chave}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            border: 1,
                                            borderColor: "divider",
                                            borderRadius: 2,
                                            height: "100%",
                                            boxSizing: "border-box"
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={700}
                                        >
                                            {grupo.rotulo}
                                        </Typography>
                                        {grupo.preparada ? (
                                            <>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {grupo.conflito
                                                        ? "Em conferência"
                                                        : grupo.servidores}{" "}
                                                    vínculos por remessa ·{" "}
                                                    {grupo.lancamentos}{" "}
                                                    lançamentos
                                                </Typography>
                                                <Stack
                                                    spacing={0.75}
                                                    sx={{my: 1.5}}
                                                >
                                                    <Valor
                                                        rotulo="Proventos"
                                                        valor={
                                                            grupo.proventosCentavos
                                                        }
                                                    />
                                                    <Valor
                                                        rotulo="Descontos"
                                                        valor={
                                                            grupo.descontosCentavos
                                                        }
                                                    />
                                                    <Valor
                                                        rotulo="Líquido"
                                                        valor={
                                                            grupo.conflito
                                                                ? null
                                                                : grupo.proventosCentavos -
                                                                  grupo.descontosCentavos
                                                        }
                                                    />
                                                </Stack>
                                                <Stack spacing={1}>
                                                    {grupo.remessas.map(
                                                        (remessa) => (
                                                            <Stack
                                                                key={remessa.id}
                                                                direction="row"
                                                                alignItems="center"
                                                                flexWrap="wrap"
                                                                sx={{gap: 1}}
                                                            >
                                                                <Link
                                                                    component={
                                                                        RouterLink
                                                                    }
                                                                    to={`/eventos/${remessa.id}`}
                                                                >
                                                                    #
                                                                    {remessa.id}
                                                                </Link>
                                                                <Chip
                                                                    size="small"
                                                                    color={
                                                                        remessa.concluida
                                                                            ? "success"
                                                                            : remessa.estado ===
                                                                              "PROCESSADO_COM_ERRO"
                                                                            ? "error"
                                                                            : "default"
                                                                    }
                                                                    label={
                                                                        remessa.concluida
                                                                            ? "Envio concluído"
                                                                            : ESTADOS[
                                                                                  remessa
                                                                                      .estado
                                                                              ] ||
                                                                              "Finalização pendente"
                                                                    }
                                                                />
                                                                <Typography variant="caption">
                                                                    {CATEGORIAS_FOLHA[
                                                                        remessa
                                                                            .categoriaOrigem
                                                                    ] ||
                                                                        remessa.categoriaOrigem ||
                                                                        "Normal"}{" "}
                                                                    · Tipo{" "}
                                                                    {
                                                                        remessa.tipo
                                                                    }{" "}
                                                                    · sequência{" "}
                                                                    {
                                                                        remessa.sequencia
                                                                    }
                                                                </Typography>
                                                                {remessa.conflito && (
                                                                    <Chip
                                                                        size="small"
                                                                        color="warning"
                                                                        label="Conferir duplicidade"
                                                                    />
                                                                )}
                                                                {remessa.arquivada && (
                                                                    <Chip
                                                                        size="small"
                                                                        variant="outlined"
                                                                        label="Arquivada — aceite preservado"
                                                                    />
                                                                )}
                                                                {Number(
                                                                    remessa.id
                                                                ) ===
                                                                    Number(
                                                                        ocorrenciaAtualId
                                                                    ) && (
                                                                    <Typography variant="caption">
                                                                        Esta
                                                                        ocorrência
                                                                    </Typography>
                                                                )}
                                                            </Stack>
                                                        )
                                                    )}
                                                </Stack>
                                            </>
                                        ) : (
                                            <Typography
                                                color="text.secondary"
                                                sx={{mt: 2}}
                                            >
                                                Não preparada — valores
                                                indisponíveis
                                            </Typography>
                                        )}
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                        <Box
                            sx={{
                                mt: 2,
                                p: 2,
                                bgcolor: "action.hover",
                                borderRadius: 2
                            }}
                        >
                            <Typography variant="subtitle2" sx={{mb: 1}}>
                                Total preparado —{" "}
                                {categoria === "TODAS"
                                    ? "todas as folhas"
                                    : CATEGORIAS_FOLHA[categoria] ||
                                      categoria}{" "}
                                — {formatarCompetencia(efetiva)}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <Valor rotulo="Proventos" valor={totalP} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Valor rotulo="Descontos" valor={totalD} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Valor
                                        rotulo="Líquido"
                                        valor={
                                            conflito ? null : totalP - totalD
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            component="p"
                            sx={{mt: 1, mb: 0}}
                        >
                            Sequências diferentes são somadas. Ocorrências
                            arquivadas com aceite oficial continuam no total até
                            o cancelamento. Arquivadas sem aceite não integram o
                            total. Aguardando aprovação não significa envio
                            concluído. O total reúne somente as ocorrências
                            preparadas da categoria selecionada; não comprova
                            cobertura integral da origem enquanto houver folhas
                            bloqueadas. Um vínculo pode aparecer em várias
                            folhas. Pensionistas judiciais de natureza alimentar
                            não integram este evento.
                        </Typography>
                    </>
                )}
            </CardContent>
        </Card>
    );
}

function Valor({rotulo, valor}) {
    return (
        <Stack direction="row" justifyContent="space-between" sx={{gap: 1}}>
            <Typography variant="body2" color="text.secondary">
                {rotulo}
            </Typography>
            <Typography
                variant="body2"
                fontWeight={700}
                sx={{fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap"}}
            >
                {valor === null ? "Em conferência" : moeda(valor)}
            </Typography>
        </Stack>
    );
}
